import {Game} from "../Game";
import {gsap} from "gsap";
import {IMonster, REEL_CORNER_NAMES} from "../server/IData";
import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";

class ReelCorner extends PIXI.Container {
    private _lifeCount: number;
    private readonly _lifeItems: PIXI.Sprite[];
    private _rcDead: PIXI.Sprite;
    private readonly _animation: PIXI.spine.Spine;
    private _healthUpAnimation: PIXI.spine.Spine;

    constructor(name: string, lifeCount: number) {
        super();

        this.name = name;

        this._lifeItems = [];
        this.createRCIcons();
        this.createLife(lifeCount);
        for (let ctr: number = 0; ctr < this._lifeItems.length; ctr++) {
            this._lifeItems[ctr].visible = true;
        }

        this._animation = new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("hps"));
        this._healthUpAnimation = new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("healthUp"));
        this._healthUpAnimation.visible = false;

        this.addChild(this._animation, this._healthUpAnimation);

        if (!lifeCount) {
            this._rcDead.alpha = 1;
            this._animation.state.setAnimation(0, "dead", false);
        }
    }

    public reduceLife(isPlayDeadSkull: boolean = false): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();

        if (this._lifeCount < 1) {
            return tl;
        }
        const item: PIXI.Sprite = this._lifeItems.pop()!;
        const heartLoseSoundStr: string = "heartLose" + (this.name.charCodeAt(0) - "A".charCodeAt(0) + 1);

        this._lifeCount--;

        tl.add(() => {
            Game.soundPlayer.playSound(heartLoseSoundStr, 1);
        })

        if (this._lifeCount === 0) {
            tl.add(() => {
                this._animation.state.setAnimation(0, "dead", false);
                if (isPlayDeadSkull) {
                    Game.soundPlayer.playSound("skullExplode" + MathHelper.randomIntInRange(1, 3));
                }
            });

            tl.to(this._rcDead, {
                duration: 0.2,
                alpha: 1
            });

            Game.model.gameData.monsters.forEach((data: IMonster, idx: number) => {
                if (this.name === REEL_CORNER_NAMES[idx]) {
                    if (!data.path || data.path.length === 0) {
                        return;
                    }
                }
            })
        } else {
            tl.add(() => {
                this._animation.state.setAnimation(0, "hp_" + (Game.LIFE_COUNT - this._lifeCount), false);
            });
        }

        tl.to(item, {
            duration: 0.2,
            alpha: 0
        });

        tl.add(() => {
            item.destroy();
        });

        return tl;
    }

    public getLife(): number {
        return this._lifeCount;
    }

    public resetLife(lifeCount: number): void {
        const prevLifeVisible: number = this._lifeCount;
        this._rcDead.alpha = 0;
        if (this._lifeCount > 2) {
            return;
        }

        this.createLife(lifeCount);

        const tl: gsap.core.Timeline = new gsap.core.Timeline();

        for (let ctr: number = prevLifeVisible; ctr < this._lifeItems.length; ctr++) {
            this._lifeItems[ctr].alpha = 0;
            this._lifeItems[ctr].visible = true;

            tl.to(this._lifeItems[ctr], {
                duration: 0.2,
                alpha: 1
            });
        }
    }

    public resetToOneLife(): void {
        const currLifeVisible: number = Game.MAYHEM_LIFE_COUNT;
        const prevLifeVisible: number = this._lifeCount;
        this._lifeCount = currLifeVisible;

        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        for (let ctr: number = currLifeVisible; ctr < prevLifeVisible; ctr++) {
            const item: PIXI.Sprite = this._lifeItems.pop()!;

            tl.to(item, {
                duration: 0.2,
                alpha: 0
            }, 0);


            tl.add(() => {
                item.destroy();
            });
        }

        tl.to(this._lifeItems[0], {
            duration: 0.2,
            alpha: 1
        });
    }

    public playHealthUpAnimation(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline({
                onComplete: () => {
                    this._healthUpAnimation.visible = false;
                }
            }),
            animTime: number = this._healthUpAnimation.spineData.findAnimation("health_up").duration;

        tl.add(() => {
            this._healthUpAnimation.visible = true;
            this._healthUpAnimation.state.setAnimation(0, "health_up", false);
        }, 0);

        tl.to(this, {
            duration: animTime
        });

        return tl;
    }

    private createLife(lifeCount: number): void {
        const pos: number[] = [-30, -10, 10];
        this._lifeCount = lifeCount;
        for (let ctr: number = this._lifeItems.length; ctr < this._lifeCount; ctr++) {
            const rcLife = new PIXI.Sprite(Game.assetLoader.getTextures("CR/CR_" + this.name + "_hp")[0]);
            rcLife.visible = false;
            rcLife.position.set(pos[ctr], -32);

            this.addChild(rcLife);
            this._lifeItems.push(rcLife);
        }
    }

    private createRCIcons(): void {
        const rc: PIXI.Sprite = new PIXI.Sprite(Game.assetLoader.getTextures("CR/CR_" + this.name)[0]);
        this._rcDead = new PIXI.Sprite(Game.assetLoader.getTextures("CR/CR_" + this.name + "_dead")[0]);
        rc.anchor.set(0.5);
        this._rcDead.anchor.set(0.5);
        this._rcDead.visible = true;
        this._rcDead.alpha = 0;
        this.addChild(rc, this._rcDead);
    }
}

export class ReelCorners extends PIXI.Container {

    private CORNERS_DEFAULT_POSITION: number[][] = [
        [68, 120], // top left
        [660, 120], // top right
        [68, 650], // bottom left
        [660, 650], // bottom right
    ];
    private readonly _corners: ReelCorner[];

    constructor() {
        super();
        this.name = "ReelCorners";

        this._corners = [];
        this.init();
    }

    public reduceLifeOfCorner(idx: number, isPlayDeadSkull: boolean = false): gsap.core.Timeline {
        return this._corners[idx].reduceLife(isPlayDeadSkull);
    }

    public resetLifeOfACorner(idx: number, lifeCount: number): void {
        this._corners[idx].resetLife(lifeCount);
    }

    public resetLifeOfAllCorners(lifeCount: number): void {
        for (let ctr: number = 0; ctr < this._corners.length; ctr++) {
            this.resetLifeOfACorner(ctr, lifeCount);
        }
    }

    public bonusBuyMayhemLifeReset(): void {
        for (let ctr: number = 0; ctr < this._corners.length; ctr++) {
            this._corners[ctr].resetToOneLife();
        }
    }

    public getLife(idx: number): number {
        return this._corners[idx].getLife();
    }

    public moveCornerOnGridExpansion(duration: number, gridName: string): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            positions: number[][] = this.getCornerPositions(gridName);

        for (let ctr: number = 0; ctr < this.CORNERS_DEFAULT_POSITION.length; ctr++) {
            const corner: ReelCorner = this._corners[ctr];
            tl.to(corner, {
                duration: duration,
                x: positions[ctr][0],
                y: positions[ctr][1]
            }, 0);
        }

        return tl;
    }

    public playHealthUpOnReelCorner(idx: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();

        tl.add(this._corners[idx].playHealthUpAnimation());

        return tl;
    }

    private init(): void {
        for (let ctr: number = 0; ctr < this.CORNERS_DEFAULT_POSITION.length; ctr++) {
            let monsterLifeCount: number = 3;
            const rcLabel: string = REEL_CORNER_NAMES[ctr];

            if (Game.model.isRestore) {
                monsterLifeCount = Game.model.initData?.monsters?.[ctr]?.lifeLeft;
            }

            const corner: ReelCorner = new ReelCorner(rcLabel, monsterLifeCount);
            corner.position.set(this.CORNERS_DEFAULT_POSITION[ctr][0], this.CORNERS_DEFAULT_POSITION[ctr][1]);

            this._corners.push(corner);
            this.addChild(corner);
        }

        if (Game.model.isRestore && Game.hasGridExpanded) {
            this.moveCornerOnGridExpansion(0, Game.ACTIVE_REELS_NUM + "by" + Game.ACTIVE_ROWS_NUM);
        }
    }

    private getCornerPositions(gridName: string): number[][] {
        switch (gridName) {
            case "8by6":
                return [
                    [31, 90], // top left
                    [690, 90], // top right
                    [31, 510], // bottom left
                    [690, 510], // bottom right
                ];
            case "6by8":
                return [
                    [31, 90], // top left
                    [515, 90], // top right
                    [31, 670], // bottom left
                    [515, 670], // bottom right
                ];
            case "8by8":
                return [
                    [32, 90], // top left
                    [690, 90], // top right
                    [32, 670], // bottom left
                    [690, 670], // bottom right
                ];
            case "9by8":
                return [
                    [34, 90], // top left
                    [677, 90], // top right
                    [34, 600], // bottom left
                    [677, 600], // bottom right
                ];
            case "8by9":
                return [
                    [35, 90], // top left
                    [600, 90], // top right
                    [35, 670], // bottom left
                    [600, 670], // bottom right
                ];
            case "9by9":
                return [
                    [34, 90], // top left
                    [677, 90], // top right
                    [34, 670], // bottom left
                    [677, 670], // bottom right
                ];
        }

        return this.CORNERS_DEFAULT_POSITION;
    }
}